/**
 * Service for the Owner Support Case Management API (032).
 *
 * Covers seven contract paths:
 *   - POST  /support/cases                      (openCase)
 *   - GET   /support/cases                      (listCases)
 *   - GET   /support/cases/:caseId              (getCase)
 *   - POST  /support/cases/:caseId/actions      (submitAction)
 *   - POST  /support/cases/:caseId/resolve      (resolveCase)
 *   - POST  /support/cases/:caseId/abandon      (abandonCase)
 *   - GET   /bookings/:bookingId/timeline       (getBookingTimeline)
 *
 * Errors are NOT swallowed — they propagate so callers can assert on
 * `response.status` and the UI can react to specific HTTP semantics.
 *
 * Feature: 032-booking-recovery-linking
 */

import { apiClient } from './api';
import type {
  SupportCase,
  SupportCaseDetail,
  SupportCaseAction,
  SupportCaseStatus,
  BookingTimelineResponse,
  OpenSupportCaseRequest,
  SubmitSupportActionRequest,
} from '../types/supportCase';

// ─── SupportCasePage ──────────────────────────────────────────────────────────

export interface SupportCasePage {
  content: SupportCase[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// ─── listCases params ─────────────────────────────────────────────────────────

interface ListCasesParams {
  bookingId?: string;
  statuses?: ReadonlyArray<SupportCaseStatus>;
  page?: number;
  size?: number;
  sort?: string;
}

// ─── supportCaseService ───────────────────────────────────────────────────────

export const supportCaseService = {
  /**
   * POST /support/cases
   * Opens a new support case for the given booking.
   * Returns 201 with the created SupportCase.
   */
  async openCase(request: OpenSupportCaseRequest): Promise<SupportCase> {
    const response = await apiClient.post<SupportCase>(
      '/support/cases',
      request,
    );
    return response.data;
  },

  /**
   * GET /support/cases
   * Lists support cases with optional filters.
   * Query parameter for status filtering is "statuses" (plural) per open-api.json.
   */
  async listCases(params?: ListCasesParams): Promise<SupportCasePage> {
    const queryParams: Record<string, unknown> = {};

    if (params?.bookingId !== undefined) {
      queryParams['bookingId'] = params.bookingId;
    }
    if (params?.statuses !== undefined) {
      queryParams['statuses'] = [...params.statuses];
    }
    if (params?.page !== undefined) {
      queryParams['page'] = params.page;
    }
    if (params?.size !== undefined) {
      queryParams['size'] = params.size;
    }
    if (params?.sort !== undefined) {
      queryParams['sort'] = params.sort;
    }

    const response = await apiClient.get<SupportCasePage>(
      '/support/cases',
      { params: queryParams },
    );
    return response.data;
  },

  /**
   * GET /support/cases/:caseId
   * Returns the full SupportCaseDetail including the actions[] history.
   */
  async getCase(caseId: string): Promise<SupportCaseDetail> {
    const response = await apiClient.get<SupportCaseDetail>(
      `/support/cases/${caseId}`,
    );
    return response.data;
  },

  /**
   * POST /support/cases/:caseId/actions
   * Submits a support action.
   * Returns 200 (synchronous outcome) or 202 (async — action still PENDING).
   */
  async submitAction(
    caseId: string,
    request: SubmitSupportActionRequest,
  ): Promise<SupportCaseAction> {
    const response = await apiClient.post<SupportCaseAction>(
      `/support/cases/${caseId}/actions`,
      request,
    );
    return response.data;
  },

  /**
   * POST /support/cases/:caseId/resolve
   * Resolves the case. Returns the updated SupportCase (status = RESOLVED).
   */
  async resolveCase(caseId: string): Promise<SupportCase> {
    const response = await apiClient.post<SupportCase>(
      `/support/cases/${caseId}/resolve`,
    );
    return response.data;
  },

  /**
   * POST /support/cases/:caseId/abandon
   * Abandons the case. Returns the updated SupportCase (status = ABANDONED).
   */
  async abandonCase(caseId: string): Promise<SupportCase> {
    const response = await apiClient.post<SupportCase>(
      `/support/cases/${caseId}/abandon`,
    );
    return response.data;
  },

  /**
   * GET /bookings/:bookingId/timeline
   * Returns the full booking journey timeline, ordered ascending by occurredAt.
   */
  async getBookingTimeline(bookingId: string): Promise<BookingTimelineResponse> {
    const response = await apiClient.get<BookingTimelineResponse>(
      `/bookings/${bookingId}/timeline`,
    );
    return response.data;
  },
};
